<template>
    <DsCombobox
        v-model:open="isOpen"
        v-model:searchTerm="searchTerm"
        :highlightOnHover="false"
        ignoreFilter
        @input="handleSearchTermChange"
    >
        <DsComboboxAnchor class="w-full">
            <DsComboboxInput asChild>
                <DsTextInput
                    v-model="searchTerm"
                    :id
                    ref="searchInput"
                    :actionableHelperText
                    :disabled
                    :error
                    :helperText
                    :label
                    :labelTooltip
                    :maxlength
                    :name
                    :placeholder
                    :required
                    :size
                    type="text"
                    :warning
                    @clickHelperText="emit('clickHelperText')"
                    @update:isFocused="handleFocusChange"
                >
                    <template #leading>
                        <DsIcon
                            icon="fa-solid fa-magnifying-glass"
                            size="sm"
                        />
                    </template>
                    <template #trailing>
                        <DsSpinner
                            v-if="isSearching"
                            size="sm"
                            variant="primary"
                        />
                        <DsIconButton
                            v-else-if="searchTerm && !disabled"
                            icon="fa-solid fa-circle-xmark"
                            size="xs"
                            tooltipText="Clear search"
                            @click.stop="handleClearSearch"
                        />
                    </template>
                </DsTextInput>
            </DsComboboxInput>
        </DsComboboxAnchor>
        <DsComboboxContent
            v-if="dropdownStatus !== 'idle'"
            class="max-h-52 overflow-y-auto"
            :sideOffset="hasMessage ? -20 : 4"
        >
            <DsSpinner
                v-if="dropdownStatus === 'loading'"
                class="grid h-52 place-items-center"
                size="lg"
            />
            <DsSearchComboboxEmptyState
                v-else-if="dropdownStatus === 'error'"
                buttonLabel="Try again"
                message="Something went wrong loading this information."
                title="Unable to load content"
                @buttonClick="$emit('performSearch', searchTerm)"
            />
            <template v-else-if="dropdownStatus === 'success'">
                <DsSearchComboboxEmptyState
                    v-if="options.length === 0"
                    buttonLabel="Clear search"
                    message="Adjust your search and try again"
                    title="No results found"
                    @buttonClick="handleClearSearch"
                />
                <DsComboboxGroup
                    v-for="group in optionGroups"
                    :key="group.label"
                    class="ds-search-combobox-group"
                >
                    <DsComboboxLabel
                        v-if="group.label"
                        class="p-3 pb-1 text-content-md font-semibold text-content-primary"
                    >
                        {{ group.label }}
                    </DsComboboxLabel>
                    <DsComboboxItem
                        v-for="(option, index) in group.options"
                        :key="index"
                        asChild
                        class="ds-search-combobox-option"
                        :value="option.value"
                        @select="handleSelectOption(option)"
                    >
                        <div class="flex w-full cursor-pointer items-center gap-3 rounded-lg p-3">
                            <p
                                class="flex-1 font-medium text-content-primary"
                                :class="size === 'sm' ? 'text-content-md' : 'text-content-lg'"
                            >
                                {{ option.label }}
                            </p>
                            <slot
                                name="optionTrailing"
                                :option
                            />
                        </div>
                    </DsComboboxItem>
                </DsComboboxGroup>
            </template>
        </DsComboboxContent>
    </DsCombobox>
</template>

<script setup lang="ts" generic="T extends DsSearchComboboxOptionValue">
import type { HelperMessageEmits } from '@/components/design-system/utils/helper-message.types';
import DsIconButton from '@ds/button/DsIconButton.vue';
import DsIcon from '@ds/DsIcon.vue';
import DsCombobox from '@ds/form/combobox/DsCombobox.vue';
import DsComboboxAnchor from '@ds/form/combobox/DsComboboxAnchor.vue';
import DsComboboxContent from '@ds/form/combobox/DsComboboxContent.vue';
import DsComboboxGroup from '@ds/form/combobox/DsComboboxGroup.vue';
import DsComboboxInput from '@ds/form/combobox/DsComboboxInput.vue';
import DsComboboxItem from '@ds/form/combobox/DsComboboxItem.vue';
import DsComboboxLabel from '@ds/form/combobox/DsComboboxLabel.vue';
import DsTextInput from '@ds/form/input/text/DsTextInput.vue';
import DsSpinner from '@ds/spinner/DsSpinner.vue';
import { debounce } from 'lodash';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import DsSearchComboboxEmptyState from './DsSearchComboboxEmptyState.vue';
import {
    DEBOUNCE_DELAY_MS,
    type DsSearchComboboxOption,
    type DsSearchComboboxOptionGroup,
    type DsSearchComboboxOptionValue,
    type DsSearchComboboxProps,
    type DsSearchComboboxSlotProps,
} from './search-combobox.types.js';

const props = defineProps<DsSearchComboboxProps<T>>();

const emit = defineEmits<
    HelperMessageEmits & {
        performSearch: [searchTerm: string];
    }
>();

defineSlots<{
    optionTrailing: (props: DsSearchComboboxSlotProps<T>) => void;
}>();

const selectedValue = defineModel<T>();
const options = defineModel<DsSearchComboboxOption<T>[]>('options', { default: () => [] });

const searchInputRef = useTemplateRef('searchInput');

const searchTerm = ref(props.initialOptionLabel ?? '');
const isOpen = ref(false);
const hasSearched = ref(false);
const selectedOption = ref<DsSearchComboboxOption<T>>();

const dropdownStatus = computed(() => {
    if (props.isSearching) return 'loading';
    if (props.searchError) return 'error';
    if (hasSearched.value || options.value.length > 0) return 'success';
    return 'idle';
});

const hasMessage = computed(() => !!props.error || !!props.warning || !!props.helperText);

const optionGroups = computed<DsSearchComboboxOptionGroup<T>[]>(() => {
    const groups = new Map<DsSearchComboboxOptionGroup<T>['label'] | undefined, DsSearchComboboxOption<T>[]>();

    options.value.forEach(option => {
        const key = option.groupLabel;

        if (!groups.has(key)) {
            groups.set(key, []);
        }

        groups.get(key)!.push(option);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
        label: label ?? '',
        options: items,
    }));
});

const handleFocusChange = (isFocused: boolean | undefined) => {
    if (isFocused && props.defaultOptions?.length) {
        isOpen.value = true;
    }
};

watch(isOpen, newIsOpen => {
    options.value = newIsOpen && props.defaultOptions ? props.defaultOptions : [];

    if (newIsOpen) return;

    hasSearched.value = false;

    if (!searchTerm.value.trim()) {
        selectedValue.value = undefined;
    } else {
        searchTerm.value = selectedOption.value?.label ?? props.initialOptionLabel ?? '';
    }
});

watch(selectedValue, () => {
    if (selectedValue.value === undefined) {
        selectedOption.value = undefined;
        searchTerm.value = '';
    }
});

const handleClearSearch = () => {
    selectedValue.value = undefined;
    options.value = [];
    isOpen.value = false;
    nextTick(() => searchInputRef.value?.inputElement?.focus());
};

const handleSearchTermChange = debounce(() => {
    if (searchTerm.value.trim()) {
        emit('performSearch', searchTerm.value);
        hasSearched.value = true;
    }
}, DEBOUNCE_DELAY_MS);

const handleSelectOption = (option: DsSearchComboboxOption<T>) => {
    selectedValue.value = option.value;
    searchTerm.value = option.label;
    selectedOption.value = option;
};
</script>

<style scoped lang="scss">
.ds-search-combobox-group {
    @apply p-2 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-ds-primary-200;
}

.ds-search-combobox-option {
    @apply hover:bg-semantic-secondary-hover active:bg-semantic-secondary-pressed data-[highlighted]:bg-semantic-secondary-hover;
}
</style>
